// Ward token mechanics. Pure. No React, no broadcasts. GameBoard
// wraps these with badge-rebuild + sync calls.
//
// ── Sorcery rule (codex "Ward") ──
// A unit or site with the Ward keyword ENTERS the realm with a Ward
// TOKEN. The token is the protection. Not the keyword itself. When
// the warded card is damaged or destroyed (by anyone) or targeted by
// an opponent's spell/ability, the TOKEN breaks INSTEAD of the
// effect resolving. Once consumed, the token is gone even though
// the keyword remains printed; the card is no longer warded.
//
// Disabled or Silenced cards lose all abilities, including the
// Ward keyword and its token.
//
// Model:
//   • The active Ward TOKEN lives in `card.statuses` only.
//   • `_printedKeywords` / `_auraStatuses` are SIGNALS to grant a
//     token on entry. They do NOT count as an active token.
//   • `_wardGranted` (boolean) flags that the printed-on-entry grant
//     has fired once for this card's current lifetime.
//   • `_wardGrantedFromAuras` (Set<sourceCardId>) tracks aura sources
//     that have granted Ward to this card during its current lifetime.
//     Prevents re-grant on every recompute tick after the player /
//     opponent consumes the token.
//   • Both flags reset when the card leaves play (caller's
//     responsibility. See clearWardGrantTracking).

import { isDisabled } from '../scope';
import { isCardSilenced } from '../../core/keywordQuery';
import { effectiveSubtypes } from '../cardFilter';
import { expandSubtypeAliases } from '../subtypeAliases';
import { countsAsMinion } from '../../core/typeline';
import { OP } from '../schema';

const WARD = 'ward';

/**
 * Codex Ward: "Evil minions can't be warded." Evil = any concrete Evil
 * subtype (the 'evil' alias expansion: demon / monster / undead), read
 * through effectiveSubtypes so a transformed identity counts. Sites,
 * Avatars and non-Evil minions ward normally.
 */
export function canBeWarded(card) {
  if (!card) return false;
  if (!countsAsMinion(card)) return true;
  const evil = new Set(expandSubtypeAliases(['evil']));
  return !effectiveSubtypes(card).some((s) => evil.has(s));
}

/**
 * Is this card currently protected by an active Ward TOKEN?
 *
 * Only the manual status set (`card.statuses`) counts. The keyword
 * itself doesn't protect once the token has been consumed. The
 * disabled override drops protection even when a token is present
 * (disabled units lose all keywords).
 */
export function hasActiveWard(card) {
  if (!card) return false;
  // Disabled or silenced units lose all keywords, Ward included (see
  // the header note). The token is masked even while present.
  if (isDisabled(card) || isCardSilenced(card)) return false;
  return (card.statuses || []).includes(WARD);
}

/**
 * Remove the Ward TOKEN from this card. Returns true when a token
 * was actually consumed so the caller can decide whether to
 * broadcast / rebuild badges.
 */
export function consumeWardStatus(card) {
  if (!card?.statuses?.includes(WARD)) return false;
  card.statuses = card.statuses.filter((s) => s !== WARD);
  // The token lives in card.statuses, but Ward is also a printed
  // keyword, so getEffectiveStatuses (the badge, the shield 3D effect,
  // the hover overlay) would re-grant it straight back from
  // _printedKeywords. Suppress it. Mirroring consumeStealth. So the
  // display agrees with the token-based hasActiveWard and a broken or
  // lost Ward visibly disappears.
  const suppressed = card._suppressedKeywords || [];
  const printedOrAura = (card._printedKeywords || []).includes(WARD)
    || (card._auraStatuses || []).includes(WARD);
  if (printedOrAura && !suppressed.includes(WARD)) {
    card._suppressedKeywords = [...suppressed, WARD];
  }
  return true;
}

/**
 * Codex Ward: "If a minion or site becomes disabled or silenced, it
 * will lose its Ward token, since Ward is an ability (similar to
 * Stealth)." State-based, permanent (the token stays gone after the
 * disable / silence ends), mirroring loseStealthIfDisabledOrSilenced.
 * Returns true when a token was actually removed.
 */
export function loseWardIfDisabledOrSilenced(card) {
  if (!card) return false;
  if (!isDisabled(card) && !isCardSilenced(card)) return false;
  return consumeWardStatus(card);
}

/**
 * Should the printed-Ward-on-entry grant fire for this card? True
 * when the card has a printed Ward keyword AND hasn't already been
 * granted its token this lifetime.
 */
export function shouldGrantWardOnEntry(card) {
  if (!card) return false;
  if (card._wardGranted) return false;
  return (card._printedKeywords || []).includes(WARD);
}

/**
 * Push the Ward TOKEN onto the card's manual status set and flag
 * the printed-entry grant as fired. Mutates in place. Used by
 * addCardToTable; mirrors the "enters the realm with a Ward token"
 * rule.
 */
export function grantWardOnEntry(card) {
  if (!card || !canBeWarded(card)) return;
  // A prior consume suppressed the printed keyword; re-entry lifts that
  // so the fresh token shows in the effective-status union (badge +
  // shield) again.
  if (card._suppressedKeywords?.includes(WARD)) {
    card._suppressedKeywords = card._suppressedKeywords.filter((s) => s !== WARD);
  }
  if (!card.statuses) card.statuses = [];
  if (!card.statuses.includes(WARD)) card.statuses.push(WARD);
  card._wardGranted = true;
}

/**
 * Aura source `sourceId` wants to grant the Ward keyword to `card`.
 * Sorcery rule: gaining the Ward keyword adds a token; the token is
 * one-shot per (target, granting source) pair. After consumption,
 * the same aura source does NOT re-grant on subsequent ticks.
 *
 * Returns true when a token was actually granted, so callers can
 * broadcast + rebuild badges. No-op (returns false) when:
 *   • the card already has a Ward token
 *   • this source has already granted to this card this lifetime
 */
export function grantWardFromAura(card, sourceId) {
  if (!card || !sourceId || !canBeWarded(card)) return false;
  if (!card._wardGrantedFromAuras) card._wardGrantedFromAuras = new Set();
  if (card._wardGrantedFromAuras.has(sourceId)) return false;
  card._wardGrantedFromAuras.add(sourceId);
  // A fresh aura grant lifts any consume suppression so the token shows.
  if (card._suppressedKeywords?.includes(WARD)) {
    card._suppressedKeywords = card._suppressedKeywords.filter((s) => s !== WARD);
  }
  if (!card.statuses) card.statuses = [];
  if (card.statuses.includes(WARD)) return false; // max one Ward per card
  card.statuses.push(WARD);
  return true;
}

/**
 * Reset both grant trackers when the card leaves play, so a fresh
 * re-entry (cemetery → replay) gets a fresh Ward token.
 */
export function clearWardGrantTracking(card) {
  if (!card) return;
  delete card._wardGranted;
  delete card._wardGrantedFromAuras;
}

// Ward break classification (codex Ward). A warded card breaks its Ward
// INSTEAD of the targeted effect resolving. Ward protects against:
//   • Damage and destruction. From ANY source (you or your opponent).
//   • Targeting via every other special ability / spell. From your
//     OPPONENT only ("You can Target your own warded units and sites
//     with your own spells and special abilities").
// Banish is removal, not "destroyed", so it's opponent-only targeting.
const WARD_BREAK_ANY_SOURCE = new Set([OP.KILL_TARGET, OP.DAMAGE_TARGET]);
const WARD_BREAK_OPPONENT_ONLY = new Set([
  OP.RETURN_TO_HAND_TARGET, OP.BANISH_TARGET,
  OP.GRANT_STATUS_TO_TARGET, OP.SWOOP_AND_BURROW_TARGET,
  OP.FLOOD_TARGET_SITE, OP.TAP_TARGET,
]);

/**
 * Does a ward-gated target op break the target's Ward? Damage /
 * destruction break it regardless of who's acting; every other targeting
 * special ability / spell breaks it only when source and target have
 * different controllers (opponent targeting). The caller still checks
 * the target actually HAS an active Ward (hasActiveWard).
 */
export function wardBreaksOnTargetOp(op, sourceCard, target) {
  if (WARD_BREAK_ANY_SOURCE.has(op)) return true;
  if (WARD_BREAK_OPPONENT_ONLY.has(op)) {
    return !!sourceCard?.rotated !== !!target?.rotated;
  }
  return false;
}
